import type { DataItemId } from '@pn/core/domain/data';
import type { Details } from '@pn/core/domain/data-info';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { isNil } from 'lodash-es';

type State = {
  isFetching: boolean;
  isError: boolean;
  idRequested: DataItemId | undefined;
  details: Details | undefined;
};

const initialState: State = {
  isFetching: false,
  isError: false,
  idRequested: undefined,
  details: undefined,
};

export const createDataDetailsSlice = (dataType: string) =>
  createSlice({
    name: `${dataType}-details`,
    initialState,
    reducers: {
      request: (state, { payload }: PayloadAction<DataItemId>) => {
        state.isFetching = true;
        state.isError = false;
        state.details =
          state.idRequested === payload ? state.details : undefined;
        state.idRequested = payload;
      },
      error: (state) => {
        state.isFetching = false;
        state.isError = true;
      },
      receive: (state, { payload }: PayloadAction<Details>) => {
        state.isFetching = false;
        state.isError = false;
        state.details = !isNil(state.details)
          ? mergeDetails(state.details, payload)
          : payload;
      },
    },
  });

function mergeDetails(prev: Details, next: Details): Details {
  if (prev.length !== next.length) {
    throw new Error(
      `Cannot merge details of different lengths: ${prev.length} vs ${next.length}`
    );
  }

  const result: Details = [];

  for (let i = 0; i < prev.length; i++) {
    const prevItem = prev[i];
    const nextItem = next[i];

    if (!isNil(nextItem.tables)) result.push(nextItem);
    else result.push(prevItem);
  }

  return result;
}
